import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  List,
  Progress,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import { CheckCircleOutlined, CopyOutlined, LinkOutlined, WarningOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatDate, formatMoney } from '@/utils/format';
import { cbsPathForCode } from '@/utils/cbsPathUtils';
import { epcWbsLibraryService } from '@/services/epcWbsLibrary.service';

const { Text, Title } = Typography;

export interface EpcWbsLibraryPackageOverviewProps {
  packageId: string;
  packageCode?: string;
  readOnly?: boolean;
  onSelectNode?: (nodeId: string) => void;
  onSelectNodeByCode?: (code: string) => void;
  onDuplicate?: () => void;
}

export const EpcWbsLibraryPackageOverview: React.FC<EpcWbsLibraryPackageOverviewProps> = ({
  packageId,
  packageCode,
  readOnly,
  onSelectNode,
  onSelectNodeByCode,
  onDuplicate,
}) => {
  const readinessQ = useQuery({
    queryKey: ['epc-wbs-lib-readiness', packageId],
    queryFn: () => epcWbsLibraryService.getPackageReadiness(packageId),
    enabled: !!packageId,
  });

  const usageQ = useQuery({
    queryKey: ['epc-wbs-lib-usage', packageId],
    queryFn: () => epcWbsLibraryService.getPackageUsage(packageId),
    enabled: !!packageId,
  });

  const cbsQ = useQuery({
    queryKey: ['epc-cbs-codes'],
    queryFn: () => epcWbsLibraryService.listCbsCodes(),
  });

  const cbsNodes = useMemo(
    () =>
      (cbsQ.data || []).map((c: any) => ({
        id: String(c.id),
        code: String(c.code),
        name: c.name,
        parent_id: c.parent_id,
      })),
    [cbsQ.data]
  );

  const data = readinessQ.data;
  if (!data) {
    return <Text type="secondary">Loading package summary…</Text>;
  }

  const maxCbs = Math.max(...data.cbsBreakdown.map((c) => c.amount), 1);

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={5} style={{ margin: 0 }}>
            Package overview
          </Title>
          <Text type="secondary">
            What gets instantiated when you plug {packageCode || 'this package'} into a BOQ line
          </Text>
        </div>
        {!readOnly && onDuplicate ? (
          <Button icon={<CopyOutlined />} onClick={onDuplicate}>
            Duplicate package
          </Button>
        ) : null}
      </div>

      <Row gutter={12}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="WBS nodes" value={data.plugPreview.wbsNodeCount} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="CWP packages" value={data.plugPreview.packageLayerCount} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Cost lines" value={data.plugPreview.costTemplateCount} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Default TEC"
              value={formatMoney(data.rolledUpDefaultCost, {
                display: 'plain',
                maximumFractionDigits: 0,
              })}
            />
          </Card>
        </Col>
      </Row>

      {data.isPlugReady ? (
        <Alert
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          message="Plug-ready"
          description="This package can be plugged into estimate BOQ lines from the BOQ line drawer."
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="Not plug-ready"
          description={
            <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              {!data.hasPackageLayerNode ? <li>Add at least one PACKAGE (CWP) node</li> : null}
              {data.packageNodesMissingTemplates.map((n) => (
                <li key={n.id}>
                  PACKAGE node{' '}
                  {onSelectNode ? (
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onSelectNode(n.id)}>
                      {n.code}
                    </Button>
                  ) : (
                    n.code
                  )}{' '}
                  needs CBS templates
                </li>
              ))}
              {data.orphanNodes.map((code) => (
                <li key={code}>
                  Fix layer hierarchy for node{' '}
                  {onSelectNodeByCode ? (
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0 }}
                      onClick={() => onSelectNodeByCode(code)}
                    >
                      {code}
                    </Button>
                  ) : (
                    code
                  )}
                </li>
              ))}
            </ul>
          }
        />
      )}

      {data.cbsBreakdown.length > 0 ? (
        <Card size="small" title="CBS cost breakdown">
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {data.cbsBreakdown.map((row) => (
              <div key={row.cbsCode}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Space size={6}>
                    <Tag>{cbsPathForCode(cbsNodes, row.cbsCode)}</Tag>
                    <Text type="secondary">{row.lineCount} line(s)</Text>
                  </Space>
                  <Text strong>
                    {formatMoney(row.amount, { display: 'plain', maximumFractionDigits: 0 })}
                  </Text>
                </div>
                <Progress
                  percent={Math.round((row.amount / maxCbs) * 100)}
                  showInfo={false}
                  size="small"
                  strokeColor={cbsColor(row.cbsCode)}
                />
              </div>
            ))}
          </Space>
        </Card>
      ) : (
        <Alert type="info" showIcon message="No CBS cost templates yet — add them on PACKAGE nodes." />
      )}

      <Card size="small" title="Estimate usage" loading={usageQ.isLoading}>
        {usageQ.data ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Row gutter={12}>
              <Col span={8}>
                <Statistic title="Estimates" value={usageQ.data.estimateCount} />
              </Col>
              <Col span={8}>
                <Statistic title="Revisions" value={usageQ.data.revisionCount} />
              </Col>
              <Col span={8}>
                <Statistic title="WBS nodes plugged" value={usageQ.data.wbsNodeCount} />
              </Col>
            </Row>
            {usageQ.data.recentEstimates.length > 0 ? (
              <List
                size="small"
                dataSource={usageQ.data.recentEstimates}
                renderItem={(row) => (
                  <List.Item
                    actions={[
                      <Link
                        key="open"
                        to={`/epc/commercial/tenders/${row.tenderId}?tab=estimate`}
                        style={{ fontSize: 12 }}
                      >
                        <LinkOutlined /> Open tender
                      </Link>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Text strong>
                          {row.estimateNumber}
                          {row.title ? ` — ${row.title}` : ''}
                        </Text>
                      }
                      description={
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {row.revisionCount} revision{row.revisionCount === 1 ? '' : 's'}
                          {row.lastPluggedAt
                            ? ` · last plugged ${formatDate(row.lastPluggedAt)}`
                            : ''}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Text type="secondary">Not plugged into any estimates yet.</Text>
            )}
          </Space>
        ) : null}
      </Card>
    </Space>
  );
};

function cbsColor(code: string): string {
  const map: Record<string, string> = {
    MAT: '#1677ff',
    LAB: '#52c41a',
    EQP: '#fa8c16',
    SUB: '#722ed1',
    OVH: '#8c8c8c',
  };
  return map[code] || '#595959';
}
